/**
 * Crazy Cribbage - AvatarManager
 * Comprehensive avatar management, preset library, storage persistence,
 * modal selection UI, and rendering for scoreboard, player zone, and game summaries.
 * Also provides patterns and canvas textures for 2D/3D peg wrapping.
 */

import { appStorage } from '../storage/IndexedDBStorage.js';

function getStorage() {
  if (typeof window !== 'undefined' && window.appStorage) return window.appStorage;
  if (appStorage) return appStorage;
  if (typeof localStorage !== 'undefined') return localStorage;
  return null;
}

/**
 * 16 Curated Cribbage-Themed Preset Avatars
 * High-contrast, vibrant, vector SVGs with 0 external network dependencies.
 */
export const PRESET_AVATARS = [
  {
    id: 'joker',
    name: 'Wild Joker',
    title: 'Court Jester & Chaos Pegger',
    badgeColor: '#a855f7',
    bgColor: 'linear-gradient(135deg, #3b0764, #7e22ce)',
    category: 'Court',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="jkGlow" cx="50%" cy="45%" r="50%">
          <stop offset="0%" stop-color="#9333ea" stop-opacity="0.8"/>
          <stop offset="100%" stop-color="#3b0764" stop-opacity="1"/>
        </radialGradient>
        <linearGradient id="jkGold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#fde047"/>
          <stop offset="100%" stop-color="#ca8a04"/>
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#jkGlow)" stroke="#facc15" stroke-width="2.5"/>
      <!-- Jester Collar / Ruffle -->
      <polygon points="50,70 40,88 50,82 60,88" fill="url(#jkGold)"/>
      <polygon points="32,72 22,85 35,80" fill="#ec4899"/>
      <polygon points="68,72 78,85 65,80" fill="#ec4899"/>
      <circle cx="22" cy="85" r="2.5" fill="#facc15"/>
      <circle cx="50" cy="82" r="3" fill="#facc15"/>
      <circle cx="78" cy="85" r="2.5" fill="#facc15"/>
      <!-- Face Mask Base -->
      <path d="M30 46 C30 36, 70 36, 70 46 C70 66, 30 66, 30 46 Z" fill="#fef08a" stroke="#ca8a04" stroke-width="1.5"/>
      <!-- Jester Hat Left Horn -->
      <path d="M34 40 C24 30, 14 36, 12 48 C18 46, 26 44, 34 42" fill="#ec4899"/>
      <circle cx="12" cy="48" r="4" fill="url(#jkGold)"/>
      <!-- Jester Hat Center Horn -->
      <path d="M42 36 C50 18, 50 16, 50 12 C52 18, 56 26, 58 36" fill="url(#jkGold)"/>
      <circle cx="50" cy="12" r="4" fill="#f43f5e"/>
      <!-- Jester Hat Right Horn -->
      <path d="M66 40 C76 30, 86 36, 88 48 C82 46, 74 44, 66 42" fill="#8b5cf6"/>
      <circle cx="88" cy="48" r="4" fill="url(#jkGold)"/>
      <!-- Hat Band -->
      <path d="M32 40 Q50 34 68 40 Q50 44 32 40 Z" fill="#ca8a04"/>
      <!-- Eyes & Smile -->
      <path d="M38 48 Q44 42 46 48" stroke="#1e1b4b" stroke-width="2" fill="none" stroke-linecap="round"/>
      <path d="M54 48 Q56 42 62 48" stroke="#1e1b4b" stroke-width="2" fill="none" stroke-linecap="round"/>
      <!-- Cheek Diamond Marks -->
      <polygon points="41,53 44,57 41,61 38,57" fill="#ec4899"/>
      <polygon points="59,53 62,57 59,61 56,57" fill="#ec4899"/>
      <!-- Mischievous Grin -->
      <path d="M38 58 Q50 70 62 58 Q50 64 38 58 Z" fill="#b91c1c"/>
      <path d="M42 59 Q50 63 58 59" stroke="#ffffff" stroke-width="1.5" fill="none"/>
    </svg>`
  },
  {
    id: 'king',
    name: 'King of Cribbage',
    title: 'Monarch of the Board',
    badgeColor: '#eab308',
    bgColor: 'linear-gradient(135deg, #7f1d1d, #b91c1c)',
    category: 'Court',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="kgGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#991b1b"/>
          <stop offset="100%" stop-color="#450a0a"/>
        </radialGradient>
        <linearGradient id="kgGold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#fef08a"/>
          <stop offset="50%" stop-color="#eab308"/>
          <stop offset="100%" stop-color="#a16207"/>
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#kgGlow)" stroke="#facc15" stroke-width="2.5"/>
      <!-- Robe Collar -->
      <path d="M22 88 Q50 78 78 88 L84 96 L16 96 Z" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1.5"/>
      <circle cx="28" cy="90" r="2" fill="#0f172a"/>
      <circle cx="50" cy="86" r="2.5" fill="#0f172a"/>
      <circle cx="72" cy="90" r="2" fill="#0f172a"/>
      <!-- King Beard -->
      <path d="M30 56 Q50 90 70 56 Q50 76 30 56 Z" fill="#fef08a" stroke="#ca8a04" stroke-width="1.5"/>
      <!-- King Face -->
      <ellipse cx="50" cy="52" rx="18" ry="16" fill="#fed7aa"/>
      <!-- Eyes & Brows -->
      <path d="M36 46 Q43 44 46 48" stroke="#78350f" stroke-width="2" fill="none"/>
      <path d="M54 48 Q57 44 64 46" stroke="#78350f" stroke-width="2" fill="none"/>
      <circle cx="42" cy="50" r="2" fill="#451a03"/>
      <circle cx="58" cy="50" r="2" fill="#451a03"/>
      <!-- Royal Mustache -->
      <path d="M34 58 Q42 54 50 58 Q58 54 66 58 Q58 64 50 60 Q42 64 34 58 Z" fill="#ca8a04"/>
      <!-- Crown Base -->
      <path d="M26 38 L30 18 L42 28 L50 14 L58 28 L70 18 L74 38 Z" fill="url(#kgGold)" stroke="#78350f" stroke-width="1.5"/>
      <rect x="25" y="36" width="50" height="6" rx="2" fill="url(#kgGold)" stroke="#78350f" stroke-width="1"/>
      <!-- Crown Jewels -->
      <circle cx="50" cy="18" r="3" fill="#ef4444"/>
      <circle cx="32" cy="22" r="2.5" fill="#3b82f6"/>
      <circle cx="68" cy="22" r="2.5" fill="#10b981"/>
      <circle cx="50" cy="39" r="2.5" fill="#ef4444"/>
      <circle cx="37" cy="39" r="2" fill="#3b82f6"/>
      <circle cx="63" cy="39" r="2" fill="#10b981"/>
    </svg>`
  },
  {
    id: 'queen',
    name: 'Queen of Skunks',
    title: 'Sovereign of Double Runs',
    badgeColor: '#10b981',
    bgColor: 'linear-gradient(135deg, #064e3b, #047857)',
    category: 'Court',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="qnGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#047857"/>
          <stop offset="100%" stop-color="#022c22"/>
        </radialGradient>
        <linearGradient id="qnGold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#fef08a"/>
          <stop offset="100%" stop-color="#d97706"/>
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#qnGlow)" stroke="#34d399" stroke-width="2.5"/>
      <!-- Hair Curls -->
      <path d="M22 48 Q20 72 32 82 Q38 68 32 52 Z" fill="#78350f"/>
      <path d="M78 48 Q80 72 68 82 Q62 68 68 52 Z" fill="#78350f"/>
      <!-- Neck & Shoulders -->
      <path d="M34 76 Q50 72 66 76 L76 96 L24 96 Z" fill="#047857" stroke="#10b981" stroke-width="1.5"/>
      <path d="M38 78 Q50 88 62 78" stroke="url(#qnGold)" stroke-width="2.5" fill="none"/>
      <!-- Queen Face -->
      <ellipse cx="50" cy="54" rx="17" ry="19" fill="#fde68a"/>
      <!-- Elegant Eyes & Lashes -->
      <path d="M37 50 Q43 46 47 51" stroke="#047857" stroke-width="2" fill="none"/>
      <path d="M53 51 Q57 46 63 50" stroke="#047857" stroke-width="2" fill="none"/>
      <line x1="35" y1="48" x2="38" y2="50" stroke="#047857" stroke-width="1.5"/>
      <line x1="65" y1="48" x2="62" y2="50" stroke="#047857" stroke-width="1.5"/>
      <!-- Ruby Lips -->
      <path d="M44 64 Q50 67 56 64 Q50 69 44 64 Z" fill="#e11d48"/>
      <!-- Tiara Crown -->
      <path d="M30 40 L34 22 L42 30 L50 16 L58 30 L66 22 L70 40 Z" fill="url(#qnGold)" stroke="#b45309" stroke-width="1.5"/>
      <circle cx="50" cy="20" r="3" fill="#10b981" stroke="#fef08a" stroke-width="1"/>
      <circle cx="36" cy="26" r="2.5" fill="#f43f5e"/>
      <circle cx="64" cy="26" r="2.5" fill="#f43f5e"/>
      <!-- Skunk Streak Accent Ribbon -->
      <path d="M47 38 L50 44 L53 38 Z" fill="#ffffff"/>
    </svg>`
  },
  {
    id: 'jack',
    name: 'His Nob Jack',
    title: 'One for His Nob',
    badgeColor: '#3b82f6',
    bgColor: 'linear-gradient(135deg, #1e3a8a, #2563eb)',
    category: 'Court',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="jkGlow2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#1d4ed8"/>
          <stop offset="100%" stop-color="#0f172a"/>
        </radialGradient>
        <linearGradient id="jkFeather" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#f43f5e"/>
          <stop offset="100%" stop-color="#fbbf24"/>
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#jkGlow2)" stroke="#60a5fa" stroke-width="2.5"/>
      <!-- Renaissance Tunic -->
      <path d="M26 84 L38 72 L62 72 L74 84 L80 96 L20 96 Z" fill="#1d4ed8" stroke="#93c5fd" stroke-width="1.5"/>
      <polygon points="50,72 44,88 56,88" fill="#fbbf24"/>
      <!-- Face -->
      <ellipse cx="50" cy="56" rx="17" ry="18" fill="#fed7aa"/>
      <circle cx="43" cy="54" r="2.5" fill="#1e3a8a"/>
      <circle cx="57" cy="54" r="2.5" fill="#1e3a8a"/>
      <!-- Smirk -->
      <path d="M43 66 Q52 70 58 64" stroke="#78350f" stroke-width="2" fill="none" stroke-linecap="round"/>
      <!-- Renaissance Beret Cap -->
      <path d="M24 44 Q50 28 76 44 Q50 38 24 44 Z" fill="#2563eb" stroke="#1d4ed8" stroke-width="1.5"/>
      <ellipse cx="50" cy="40" rx="28" ry="10" fill="#1e40af"/>
      <!-- Feather Plume (His Nob) -->
      <path d="M68 38 Q82 18 64 8 Q74 24 64 36" fill="url(#jkFeather)" stroke="#f59e0b" stroke-width="1"/>
      <!-- Peg Brooch -->
      <circle cx="66" cy="38" r="4.5" fill="#fbbf24" stroke="#78350f" stroke-width="1"/>
      <rect x="64.5" y="35" width="3" height="6" rx="1" fill="#dc2626"/>
    </svg>`
  },
  {
    id: 'shark',
    name: 'The Peg Shark',
    title: 'Relentless Board Hunter',
    badgeColor: '#06b6d4',
    bgColor: 'linear-gradient(135deg, #083344, #0e7490)',
    category: 'Wildlife',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="shkGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#0891b2"/>
          <stop offset="100%" stop-color="#082f49"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#shkGlow)" stroke="#22d3ee" stroke-width="2.5"/>
      <!-- Ocean Waves Backing -->
      <path d="M12 72 Q30 64 50 72 Q70 80 88 72 L88 96 L12 96 Z" fill="#0369a1" opacity="0.6"/>
      <!-- Dorsal Fin Behind -->
      <path d="M48 14 Q52 24 62 30 L44 32 Z" fill="#0f766e"/>
      <!-- Shark Head Silhouette -->
      <path d="M22 66 C22 36, 46 26, 78 38 C74 54, 66 70, 48 76 C32 78, 22 72, 22 66 Z" fill="#64748b" stroke="#cbd5e1" stroke-width="1.5"/>
      <!-- Belly Accent -->
      <path d="M30 68 C34 56, 52 58, 68 54 C58 68, 44 74, 30 68 Z" fill="#f1f5f9"/>
      <!-- Shark Eye (Menacing) -->
      <circle cx="64" cy="42" r="4.5" fill="#0f172a"/>
      <circle cx="65.5" cy="41" r="1.5" fill="#facc15"/>
      <!-- Sharp Teeth Grin -->
      <path d="M42 60 Q56 62 70 52" stroke="#0f172a" stroke-width="2" fill="none"/>
      <polygon points="48,60 50,56 52,60" fill="#ffffff"/>
      <polygon points="54,59 56,55 58,59" fill="#ffffff"/>
      <polygon points="60,57 62,53 64,57" fill="#ffffff"/>
      <!-- Golden Peg in Jaws -->
      <path d="M34 52 L56 64" stroke="#facc15" stroke-width="4.5" stroke-linecap="round"/>
      <circle cx="34" cy="52" r="3.5" fill="#eab308"/>
    </svg>`
  },
  {
    id: 'turtler',
    name: 'Patient Turtler',
    title: 'Slow, Steady & Unskunkable',
    badgeColor: '#22c55e',
    bgColor: 'linear-gradient(135deg, #14532d, #15803d)',
    category: 'Wildlife',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="trtGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#16a34a"/>
          <stop offset="100%" stop-color="#052e16"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#trtGlow)" stroke="#4ade80" stroke-width="2.5"/>
      <!-- Turtle Shell (Cribbage Board Track Motif) -->
      <ellipse cx="50" cy="54" rx="34" ry="28" fill="#713f12" stroke="#ca8a04" stroke-width="2"/>
      <!-- Shell Track Lines -->
      <ellipse cx="50" cy="54" rx="26" ry="20" fill="#854d0e" stroke="#eab308" stroke-width="1.5" stroke-dasharray="3,3"/>
      <ellipse cx="50" cy="54" rx="16" ry="12" fill="#a16207" stroke="#fde047" stroke-width="1.5"/>
      <!-- Pegs sticking out of Shell -->
      <circle cx="44" cy="48" r="3" fill="#ef4444" stroke="#fef08a" stroke-width="1"/>
      <circle cx="56" cy="48" r="3" fill="#3b82f6" stroke="#fef08a" stroke-width="1"/>
      <circle cx="50" cy="58" r="3" fill="#22c55e" stroke="#fef08a" stroke-width="1"/>
      <!-- Turtle Head -->
      <ellipse cx="50" cy="24" rx="14" ry="12" fill="#4ade80" stroke="#166534" stroke-width="1.5"/>
      <!-- Wise Big Eyes -->
      <circle cx="44" cy="22" r="3.5" fill="#ffffff"/>
      <circle cx="44" cy="22" r="2" fill="#14532d"/>
      <circle cx="56" cy="22" r="3.5" fill="#ffffff"/>
      <circle cx="56" cy="22" r="2" fill="#14532d"/>
      <!-- Gentle Smile -->
      <path d="M46 28 Q50 32 54 28" stroke="#14532d" stroke-width="1.5" fill="none" stroke-linecap="round"/>
      <!-- Little Flippers -->
      <ellipse cx="18" cy="44" rx="7" ry="5" fill="#4ade80" transform="rotate(-25 18 44)"/>
      <ellipse cx="82" cy="44" rx="7" ry="5" fill="#4ade80" transform="rotate(25 82 44)"/>
    </svg>`
  },
  {
    id: 'gambler',
    name: 'High Roller',
    title: 'Double-Run Risk Taker',
    badgeColor: '#f43f5e',
    bgColor: 'linear-gradient(135deg, #4c0519, #881337)',
    category: 'Vegas & Pub',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="gmbGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#be123c"/>
          <stop offset="100%" stop-color="#4c0519"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#gmbGlow)" stroke="#fb7185" stroke-width="2.5"/>
      <!-- Silk Top Hat -->
      <path d="M28 48 L32 16 L68 16 L72 48 Z" fill="#0f172a" stroke="#334155" stroke-width="1.5"/>
      <ellipse cx="50" cy="16" rx="18" ry="4" fill="#1e293b"/>
      <!-- Crimson Ribbon Band -->
      <path d="M29 42 L30 48 L70 48 L71 42 Z" fill="#e11d48"/>
      <!-- Wide Brim -->
      <ellipse cx="50" cy="50" rx="36" ry="8" fill="#0f172a" stroke="#334155" stroke-width="1.5"/>
      <!-- Playing Card tucked into Ribbon -->
      <g transform="translate(56, 26) rotate(15)">
        <rect x="0" y="0" width="14" height="20" rx="2" fill="#ffffff" stroke="#94a3b8" stroke-width="0.75"/>
        <text x="3" y="7" font-size="6" font-weight="bold" fill="#dc2626" font-family="sans-serif">A</text>
        <polygon points="7,9 10,14 7,19 4,14" fill="#dc2626"/>
      </g>
      <!-- Pair of Red Dice below -->
      <g transform="translate(26, 62) rotate(-8)">
        <rect x="0" y="0" width="22" height="22" rx="4" fill="#e11d48" stroke="#ffffff" stroke-width="1.5"/>
        <circle cx="5" cy="5" r="2" fill="#ffffff"/>
        <circle cx="17" cy="5" r="2" fill="#ffffff"/>
        <circle cx="11" cy="11" r="2" fill="#ffffff"/>
        <circle cx="5" cy="17" r="2" fill="#ffffff"/>
        <circle cx="17" cy="17" r="2" fill="#ffffff"/>
      </g>
      <g transform="translate(54, 64) rotate(12)">
        <rect x="0" y="0" width="20" height="20" rx="4" fill="#e11d48" stroke="#ffffff" stroke-width="1.5"/>
        <circle cx="5" cy="5" r="2" fill="#ffffff"/>
        <circle cx="15" cy="5" r="2" fill="#ffffff"/>
        <circle cx="5" cy="10" r="2" fill="#ffffff"/>
        <circle cx="15" cy="10" r="2" fill="#ffffff"/>
        <circle cx="5" cy="15" r="2" fill="#ffffff"/>
        <circle cx="15" cy="15" r="2" fill="#ffffff"/>
      </g>
    </svg>`
  },
  {
    id: 'ace_spades',
    name: 'Ace of Spades',
    title: 'The Legendary Death Card',
    badgeColor: '#fbbf24',
    bgColor: 'linear-gradient(135deg, #18181b, #27272a)',
    category: 'Legendary',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="aceGlow" cx="50%" cy="45%" r="50%">
          <stop offset="0%" stop-color="#3f3f46"/>
          <stop offset="100%" stop-color="#09090b"/>
        </radialGradient>
        <linearGradient id="aceGold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#fef08a"/>
          <stop offset="50%" stop-color="#eab308"/>
          <stop offset="100%" stop-color="#a16207"/>
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#aceGlow)" stroke="url(#aceGold)" stroke-width="2.5"/>
      <!-- Ornate Laurel Wreath -->
      <path d="M22 66 C14 42, 28 22, 50 18 C72 22, 86 42, 78 66" stroke="#ca8a04" stroke-width="2" fill="none" stroke-dasharray="4,3"/>
      <!-- Large Filigree Ace of Spades -->
      <path d="M50 24 C44 38, 22 48, 22 64 C22 74, 34 80, 44 74 C47 72, 48 70, 48 70 L46 84 L54 84 L52 70 C52 70, 53 72, 56 74 C66 80, 78 74, 78 64 C78 48, 56 38, 50 24 Z" fill="url(#aceGold)" stroke="#78350f" stroke-width="1.5"/>
      <!-- Center Emblem Inner Cutout -->
      <path d="M50 36 C46 46, 32 54, 32 63 C32 69, 40 71, 46 67 C48 65, 49 63, 50 62 C51 63, 52 65, 54 67 C60 71, 68 69, 68 63 C68 54, 54 46, 50 36 Z" fill="#09090b"/>
      <circle cx="50" cy="54" r="4" fill="url(#aceGold)"/>
    </svg>`
  },
  {
    id: 'pint_master',
    name: 'Pub Master Dave',
    title: 'The Alehouse Skunker',
    badgeColor: '#f59e0b',
    bgColor: 'linear-gradient(135deg, #451a03, #78350f)',
    category: 'Vegas & Pub',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="pntGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#b45309"/>
          <stop offset="100%" stop-color="#451a03"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#pntGlow)" stroke="#f59e0b" stroke-width="2.5"/>
      <!-- Crossed Cribbage Pegs behind Pint -->
      <line x1="20" y1="20" x2="80" y2="80" stroke="#fde047" stroke-width="3.5" stroke-linecap="round"/>
      <line x1="80" y1="20" x2="20" y2="80" stroke="#fde047" stroke-width="3.5" stroke-linecap="round"/>
      <!-- Pint Tankard Body -->
      <rect x="34" y="36" width="32" height="44" rx="4" fill="#d97706" stroke="#fbbf24" stroke-width="2"/>
      <!-- Amber Ale Contents -->
      <rect x="36" y="42" width="28" height="36" rx="2" fill="#b45309"/>
      <!-- Frosted Tankard Ribs -->
      <line x1="43" y1="44" x2="43" y2="76" stroke="#f59e0b" stroke-width="1.5" opacity="0.6"/>
      <line x1="50" y1="44" x2="50" y2="76" stroke="#f59e0b" stroke-width="1.5" opacity="0.6"/>
      <line x1="57" y1="44" x2="57" y2="76" stroke="#f59e0b" stroke-width="1.5" opacity="0.6"/>
      <!-- Tankard Handle -->
      <path d="M66 44 Q80 44 80 58 Q80 72 66 72" stroke="#fbbf24" stroke-width="4.5" fill="none" stroke-linecap="round"/>
      <!-- Foaming Beer Head (Puffy Bubbles) -->
      <circle cx="34" cy="36" r="6" fill="#fef08a"/>
      <circle cx="42" cy="32" r="7" fill="#ffffff"/>
      <circle cx="50" cy="30" r="8" fill="#ffffff"/>
      <circle cx="58" cy="32" r="7" fill="#ffffff"/>
      <circle cx="66" cy="36" r="6" fill="#fef08a"/>
      <!-- Dripping Foam -->
      <path d="M40 38 Q42 46 44 40" fill="#ffffff"/>
      <path d="M52 38 Q54 48 56 40" fill="#ffffff"/>
    </svg>`
  },
  {
    id: 'steampunk',
    name: 'Clockwork Pegger',
    title: 'Precision Brass & Cog Mechanist',
    badgeColor: '#d97706',
    bgColor: 'linear-gradient(135deg, #292524, #44403c)',
    category: 'Legendary',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="spkGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#78716c"/>
          <stop offset="100%" stop-color="#1c1917"/>
        </radialGradient>
        <linearGradient id="spkBrass" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#fde047"/>
          <stop offset="100%" stop-color="#b45309"/>
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#spkGlow)" stroke="url(#spkBrass)" stroke-width="2.5"/>
      <!-- Large Brass Gear (Left) -->
      <g transform="translate(30, 62)">
        <circle cx="0" cy="0" r="14" fill="url(#spkBrass)" stroke="#78350f" stroke-width="1.5"/>
        <circle cx="0" cy="0" r="6" fill="#1c1917"/>
        <!-- Gear Cogs -->
        <rect x="-2" y="-18" width="4" height="6" fill="url(#spkBrass)"/>
        <rect x="-2" y="12" width="4" height="6" fill="url(#spkBrass)"/>
        <rect x="-18" y="-2" width="6" height="4" fill="url(#spkBrass)"/>
        <rect x="12" y="-2" width="6" height="4" fill="url(#spkBrass)"/>
      </g>
      <!-- Medium Brass Gear (Right) -->
      <g transform="translate(68, 64)">
        <circle cx="0" cy="0" r="11" fill="url(#spkBrass)" stroke="#78350f" stroke-width="1.5"/>
        <circle cx="0" cy="0" r="4" fill="#1c1917"/>
        <rect x="-1.5" y="-14" width="3" height="4" fill="url(#spkBrass)"/>
        <rect x="-1.5" y="10" width="3" height="4" fill="url(#spkBrass)"/>
        <rect x="-14" y="-1.5" width="4" height="3" fill="url(#spkBrass)"/>
        <rect x="10" y="-1.5" width="4" height="3" fill="url(#spkBrass)"/>
      </g>
      <!-- Steampunk Goggles -->
      <rect x="22" y="34" width="56" height="6" rx="2" fill="#78350f"/>
      <!-- Left Lens -->
      <circle cx="36" cy="37" r="14" fill="#ca8a04" stroke="url(#spkBrass)" stroke-width="3"/>
      <circle cx="36" cy="37" r="10" fill="#06b6d4" opacity="0.85"/>
      <circle cx="34" cy="35" r="3" fill="#ffffff" opacity="0.6"/>
      <!-- Right Lens -->
      <circle cx="64" cy="37" r="14" fill="#ca8a04" stroke="url(#spkBrass)" stroke-width="3"/>
      <circle cx="64" cy="37" r="10" fill="#06b6d4" opacity="0.85"/>
      <circle cx="62" cy="35" r="3" fill="#ffffff" opacity="0.6"/>
      <!-- Center Bridge -->
      <rect x="46" y="34" width="8" height="6" fill="url(#spkBrass)"/>
      <!-- Steam Pressure Gauge Needle -->
      <line x1="36" y1="37" x2="41" y2="33" stroke="#ef4444" stroke-width="1.5"/>
    </svg>`
  },
  {
    id: 'clash_helm',
    name: 'Board Gladiator',
    title: 'Champion of Cribbage Clash',
    badgeColor: '#eab308',
    bgColor: 'linear-gradient(135deg, #713f12, #a16207)',
    category: 'Vegas & Pub',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="clshGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#b45309"/>
          <stop offset="100%" stop-color="#451a03"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#clshGlow)" stroke="#facc15" stroke-width="2.5"/>
      <!-- Red Feather Crest Plume -->
      <path d="M46 10 Q50 6 54 10 L54 36 L46 36 Z" fill="#dc2626"/>
      <path d="M38 16 Q50 8 62 16 L54 36 L46 36 Z" fill="#ef4444"/>
      <!-- Golden Spartan / Gladiator Helmet Dome -->
      <path d="M26 44 C26 26, 74 26, 74 44 L74 62 L64 62 L64 52 L36 52 L36 62 L26 62 Z" fill="#eab308" stroke="#78350f" stroke-width="1.5"/>
      <!-- Cheek Guards -->
      <path d="M28 58 L36 78 L44 64 L36 58 Z" fill="#ca8a04" stroke="#78350f" stroke-width="1"/>
      <path d="M72 58 L64 78 L56 64 L64 58 Z" fill="#ca8a04" stroke="#78350f" stroke-width="1"/>
      <!-- Nose Guard & Eye Slits -->
      <rect x="47" y="44" width="6" height="24" fill="#a16207"/>
      <!-- Dark Eye Cavity -->
      <rect x="36" y="46" width="11" height="6" rx="1" fill="#1e1b4b"/>
      <rect x="53" y="46" width="11" height="6" rx="1" fill="#1e1b4b"/>
      <!-- Glowing Gladiator Eyes -->
      <circle cx="41" cy="49" r="1.5" fill="#fef08a"/>
      <circle cx="59" cy="49" r="1.5" fill="#fef08a"/>
    </svg>`
  },
  {
    id: 'velvet_fox',
    name: 'Sly Fox',
    title: 'The Mastermind Pegger',
    badgeColor: '#f97316',
    bgColor: 'linear-gradient(135deg, #7c2d12, #c2410c)',
    category: 'Wildlife',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="foxGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#ea580c"/>
          <stop offset="100%" stop-color="#431407"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#foxGlow)" stroke="#fb923c" stroke-width="2.5"/>
      <!-- Fox Ears Outer -->
      <polygon points="24,42 32,14 46,36" fill="#ea580c" stroke="#9a3412" stroke-width="1.5"/>
      <polygon points="76,42 68,14 54,36" fill="#ea580c" stroke="#9a3412" stroke-width="1.5"/>
      <!-- Fox Ears Inner (Fluffy) -->
      <polygon points="28,38 34,20 42,34" fill="#fed7aa"/>
      <polygon points="72,38 66,20 58,34" fill="#fed7aa"/>
      <!-- Fox Face Main (Orange Fur) -->
      <path d="M22 46 C22 36, 78 36, 78 46 C78 64, 50 82, 50 82 C50 82, 22 64, 22 46 Z" fill="#ea580c"/>
      <!-- White Cheeks & Snout -->
      <path d="M22 46 C34 58, 44 72, 50 82 C56 72, 66 58, 78 46 C74 58, 62 68, 50 78 C38 68, 26 58, 22 46 Z" fill="#ffffff"/>
      <!-- Mischievous Eyes (Left Eye Winking) -->
      <path d="M34 50 Q40 44 44 50" stroke="#431407" stroke-width="2.5" fill="none" stroke-linecap="round"/>
      <circle cx="60" cy="48" r="3" fill="#431407"/>
      <circle cx="61" cy="47" r="1" fill="#ffffff"/>
      <!-- Black Nose -->
      <polygon points="47,78 53,78 50,82" fill="#18181b"/>
      <!-- Whiskers -->
      <line x1="26" y1="62" x2="16" y2="60" stroke="#fdba74" stroke-width="1.5"/>
      <line x1="26" y1="66" x2="16" y2="68" stroke="#fdba74" stroke-width="1.5"/>
      <line x1="74" y1="62" x2="84" y2="60" stroke="#fdba74" stroke-width="1.5"/>
      <line x1="74" y1="66" x2="84" y2="68" stroke="#fdba74" stroke-width="1.5"/>
    </svg>`
  },
  {
    id: 'card_assassin',
    name: 'Card Assassin',
    title: 'Silent Crib Striker',
    badgeColor: '#8b5cf6',
    bgColor: 'linear-gradient(135deg, #09090b, #18181b)',
    category: 'Legendary',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="asnGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#4c1d95"/>
          <stop offset="100%" stop-color="#09090b"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#asnGlow)" stroke="#a78bfa" stroke-width="2.5"/>
      <!-- Crossed Razor Card Blades in Background -->
      <g transform="translate(50, 48) rotate(45)">
        <rect x="-14" y="-22" width="28" height="44" rx="2" fill="#3b82f6" stroke="#93c5fd" stroke-width="1.5" opacity="0.7"/>
      </g>
      <g transform="translate(50, 48) rotate(-45)">
        <rect x="-14" y="-22" width="28" height="44" rx="2" fill="#ef4444" stroke="#fca5a5" stroke-width="1.5" opacity="0.7"/>
      </g>
      <!-- Assassin Cowl / Hood -->
      <path d="M50 14 C30 18, 20 44, 20 72 L32 86 L50 78 L68 86 L80 72 C80 44, 70 18, 50 14 Z" fill="#18181b" stroke="#6d28d9" stroke-width="2"/>
      <!-- Shadowy Face Hollow -->
      <path d="M50 26 C36 32, 32 50, 32 66 L50 72 L68 66 C68 50, 64 32, 50 26 Z" fill="#09090b"/>
      <!-- Glowing Lethal Eyes -->
      <ellipse cx="42" cy="50" rx="4" ry="2" fill="#a855f7"/>
      <ellipse cx="58" cy="50" rx="4" ry="2" fill="#a855f7"/>
      <circle cx="42" cy="50" r="1.5" fill="#f5d0fe"/>
      <circle cx="58" cy="50" r="1.5" fill="#f5d0fe"/>
      <!-- Face Mask Wrap -->
      <path d="M34 58 Q50 64 66 58 L50 72 Z" fill="#2e1065"/>
    </svg>`
  },
  {
    id: 'cannon_commander',
    name: 'Captain 31',
    title: 'High Seas 31 Blaster',
    badgeColor: '#e11d48',
    bgColor: 'linear-gradient(135deg, #1e293b, #0f172a)',
    category: 'Legendary',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="cptGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#334155"/>
          <stop offset="100%" stop-color="#020617"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#cptGlow)" stroke="#f43f5e" stroke-width="2.5"/>
      <!-- Pirate Bicorne Hat -->
      <path d="M12 44 Q50 18 88 44 Q50 36 12 44 Z" fill="#0f172a" stroke="#ca8a04" stroke-width="2"/>
      <ellipse cx="50" cy="36" rx="20" ry="12" fill="#1e293b"/>
      <!-- Skull with Crossed Pegs Insignia -->
      <circle cx="50" cy="32" r="5" fill="#f8fafc"/>
      <line x1="42" y1="38" x2="58" y2="26" stroke="#fde047" stroke-width="2" stroke-linecap="round"/>
      <line x1="58" y1="38" x2="42" y2="26" stroke="#fde047" stroke-width="2" stroke-linecap="round"/>
      <!-- Face with Eyepatch -->
      <ellipse cx="50" cy="56" rx="16" ry="17" fill="#fed7aa"/>
      <circle cx="42" cy="52" r="2.5" fill="#020617"/>
      <!-- Leather Eyepatch -->
      <path d="M36 48 L64 54" stroke="#020617" stroke-width="2"/>
      <circle cx="58" cy="52" r="4.5" fill="#020617"/>
      <!-- Pirate Beard -->
      <path d="M34 60 Q50 82 66 60 Q50 72 34 60 Z" fill="#1e293b"/>
      <!-- Smoking Brass Cannon below -->
      <g transform="translate(50, 82)">
        <rect x="-16" y="-6" width="32" height="12" rx="3" fill="#ca8a04" stroke="#78350f" stroke-width="1.5"/>
        <circle cx="16" cy="0" r="4" fill="#020617"/>
      </g>
    </svg>`
  },
  {
    id: 'lightning_29',
    name: 'The 29 Club',
    title: 'The Holy Grail of Cribbage',
    badgeColor: '#38bdf8',
    bgColor: 'linear-gradient(135deg, #0369a1, #0284c7)',
    category: 'Legendary',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="ltnGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#38bdf8"/>
          <stop offset="100%" stop-color="#0c4a6e"/>
        </radialGradient>
        <linearGradient id="ltnBolt" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#fef08a"/>
          <stop offset="50%" stop-color="#facc15"/>
          <stop offset="100%" stop-color="#ea580c"/>
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#ltnGlow)" stroke="#7dd3fc" stroke-width="2.5"/>
      <!-- Electric Heraldic Shield -->
      <path d="M26 24 L74 24 L74 54 C74 72, 50 86, 50 86 C50 86, 26 72, 26 54 Z" fill="#0f172a" stroke="#38bdf8" stroke-width="2"/>
      <!-- Glowing 29 Text -->
      <text x="50" y="58" font-size="28" font-weight="900" text-anchor="middle" fill="#38bdf8" font-family="'Impact', 'Arial Black', sans-serif" letter-spacing="1">29</text>
      <!-- Electric Crackling Lightning Bolt across shield -->
      <polygon points="56,12 36,46 48,46 42,78 66,38 52,38" fill="url(#ltnBolt)" stroke="#ffffff" stroke-width="1"/>
    </svg>`
  },
  {
    id: 'lucky_clover',
    name: 'Emerald Clover',
    title: 'Blessed by the Cut',
    badgeColor: '#10b981',
    bgColor: 'linear-gradient(135deg, #064e3b, #047857)',
    category: 'Legendary',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="clvGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#10b981"/>
          <stop offset="100%" stop-color="#022c22"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#clvGlow)" stroke="#6ee7b7" stroke-width="2.5"/>
      <!-- Golden Horseshoe Backdrop -->
      <path d="M30 70 C24 48, 24 30, 40 22 C56 16, 70 24, 72 40 C74 54, 68 70, 68 70" stroke="#facc15" stroke-width="6" fill="none" stroke-linecap="round"/>
      <!-- Four-Leaf Clover Leaves -->
      <circle cx="40" cy="40" r="13" fill="#22c55e" stroke="#15803d" stroke-width="1.5"/>
      <circle cx="60" cy="40" r="13" fill="#22c55e" stroke="#15803d" stroke-width="1.5"/>
      <circle cx="40" cy="60" r="13" fill="#22c55e" stroke="#15803d" stroke-width="1.5"/>
      <circle cx="60" cy="60" r="13" fill="#22c55e" stroke="#15803d" stroke-width="1.5"/>
      <!-- Clover Center Heart Lines -->
      <circle cx="50" cy="50" r="6" fill="#16a34a"/>
      <!-- Clover Stem -->
      <path d="M50 54 Q48 76 38 88" stroke="#15803d" stroke-width="3" fill="none" stroke-linecap="round"/>
      <!-- Dewdrop Sparkle -->
      <circle cx="37" cy="37" r="2.5" fill="#ffffff" opacity="0.8"/>
    </svg>`
  }
];

export class AvatarManager {
  static STORAGE_KEY = 'cc_player_avatar';
  static DEFAULT_AVATAR_ID = 'joker';
  static CUSTOM_AVATAR_ID = 'custom';
  static STORAGE_KEY_CUSTOM = 'cc_custom_avatar_data';
  static STORAGE_KEY_CUSTOM_NAME = 'cc_custom_avatar_name';
  static _activeModal = null;
  static _remoteAvatars = new Map();
  static _inMemoryCustomData = null;
  static _inMemoryCustomName = null;

  /**
   * Retrieves full list of available preset avatars.
   * @returns {Array<Object>}
   */
  static getAvatars() {
    return PRESET_AVATARS;
  }

  /**
   * Finds avatar by unique ID. Supports custom uploaded photo, remote peers, and vector presets.
   * @param {string} id
   * @returns {Object}
   */
  static getAvatarById(id, storage = null) {
    if (!id) return PRESET_AVATARS[0];
    const cleanId = String(id).toLowerCase().trim();

    // 1. Local custom photo avatar
    if (cleanId === 'custom') {
      const data = AvatarManager.getCustomAvatarData(storage);
      if (data) {
        return {
          id: 'custom',
          name: AvatarManager.getCustomAvatarName(storage) || 'My Photo',
          title: 'Custom Cribbage Persona',
          badgeColor: '#f59e0b',
          bgColor: 'linear-gradient(135deg, #1e1b4b, #312e81)',
          category: 'Custom',
          isCustom: true,
          dataUrl: data,
          svg: AvatarManager.generateCustomAvatarSvg(data)
        };
      }
      return PRESET_AVATARS[0];
    }

    // 2. Remote peer custom avatar (custom_p2, custom_p3, etc.)
    if (cleanId.startsWith('custom_')) {
      if (AvatarManager._remoteAvatars && AvatarManager._remoteAvatars.has(cleanId)) {
        return AvatarManager._remoteAvatars.get(cleanId);
      }
      const localData = AvatarManager.getCustomAvatarData(storage);
      return {
        id: cleanId,
        name: 'Remote Player',
        title: 'Remote Cribbage Persona',
        badgeColor: '#f59e0b',
        bgColor: 'linear-gradient(135deg, #1e1b4b, #312e81)',
        category: 'Custom',
        isCustom: true,
        dataUrl: localData || null,
        svg: AvatarManager.generateCustomAvatarSvg(localData || null)
      };
    }

    // 3. Preset vector avatar library
    const found = PRESET_AVATARS.find(a => a.id === cleanId);
    return found || PRESET_AVATARS[0];
  }

  /**
   * Retrieves the custom photo Data URL from storage.
   * @param {Object} [storage=null]
   * @returns {string|null}
   */
  static getCustomAvatarData(storage = null) {
    if (AvatarManager._inMemoryCustomData) return AvatarManager._inMemoryCustomData;
    const s = storage || getStorage();
    if (!s) return null;
    try {
      const val = s.getItem(AvatarManager.STORAGE_KEY_CUSTOM);
      if (val && typeof val === 'string') return val;
    } catch (_) {}
    if (typeof localStorage !== 'undefined' && s !== localStorage) {
      try {
        const val = localStorage.getItem(AvatarManager.STORAGE_KEY_CUSTOM);
        if (val && typeof val === 'string') return val;
      } catch (_) {}
    }
    return null;
  }

  /**
   * Retrieves the custom persona label/name from storage.
   * @param {Object} [storage=null]
   * @returns {string}
   */
  static getCustomAvatarName(storage = null) {
    if (AvatarManager._inMemoryCustomName) return AvatarManager._inMemoryCustomName;
    const s = storage || getStorage();
    if (!s) return 'My Photo';
    try {
      const val = s.getItem(AvatarManager.STORAGE_KEY_CUSTOM_NAME);
      if (val && typeof val === 'string' && val.trim()) return val.trim();
    } catch (_) {}
    if (typeof localStorage !== 'undefined' && s !== localStorage) {
      try {
        const val = localStorage.getItem(AvatarManager.STORAGE_KEY_CUSTOM_NAME);
        if (val && typeof val === 'string' && val.trim()) return val.trim();
      } catch (_) {}
    }
    return 'My Photo';
  }

  /**
   * Saves a custom photo Data URL and sets it as the active avatar.
   * @param {string} dataUrl
   * @param {string} [name='My Photo']
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  static setCustomAvatar(dataUrl, name = 'My Photo', storage = null) {
    if (!dataUrl) return null;
    AvatarManager._inMemoryCustomData = dataUrl;
    AvatarManager._inMemoryCustomName = name;
    const s = storage || getStorage();
    if (s) {
      try {
        s.setItem(AvatarManager.STORAGE_KEY_CUSTOM, dataUrl);
        s.setItem(AvatarManager.STORAGE_KEY_CUSTOM_NAME, name);
      } catch (e) {
        console.warn('[AvatarManager] Custom avatar storage write warning:', e);
      }
    }
    if (typeof localStorage !== 'undefined' && s !== localStorage) {
      try {
        localStorage.setItem(AvatarManager.STORAGE_KEY_CUSTOM, dataUrl);
        localStorage.setItem(AvatarManager.STORAGE_KEY_CUSTOM_NAME, name);
      } catch (_) {}
    }

    AvatarManager._medallionCache?.clear();
    AvatarManager._bodyWrapCache?.clear();
    AvatarManager._canvasCache?.clear();

    AvatarManager.setSelectedAvatarId('custom', storage);
    return AvatarManager.getAvatarById('custom');
  }

  /**
   * Clears custom photo from storage and reverts to default avatar.
   * @param {Object} [storage=null]
   */
  static clearCustomAvatar(storage = null) {
    AvatarManager._inMemoryCustomData = null;
    AvatarManager._inMemoryCustomName = null;
    const s = storage || getStorage();
    if (s) {
      try {
        s.removeItem(AvatarManager.STORAGE_KEY_CUSTOM);
        s.removeItem(AvatarManager.STORAGE_KEY_CUSTOM_NAME);
      } catch (_) {}
    }
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem(AvatarManager.STORAGE_KEY_CUSTOM);
        localStorage.removeItem(AvatarManager.STORAGE_KEY_CUSTOM_NAME);
      } catch (_) {}
    }

    AvatarManager._medallionCache?.clear();
    AvatarManager._bodyWrapCache?.clear();
    AvatarManager._canvasCache?.clear();

    if (AvatarManager.getSelectedAvatarId(storage) === 'custom') {
      AvatarManager.setSelectedAvatarId(AvatarManager.DEFAULT_AVATAR_ID, storage);
    } else {
      AvatarManager.syncAvatarUI(storage);
    }
  }

  /**
   * Registers a remote player's custom avatar photo for multiplayer sync.
   * @param {string} seatId - e.g. 'p2', 'p3'
   * @param {string} dataUrl - Compressed photo data URL
   * @param {string} [name=null]
   * @returns {Object}
   */
  static registerRemoteCustomAvatar(seatId, dataUrl, name = null) {
    if (!seatId || !dataUrl) return null;
    const cleanSeat = String(seatId).toLowerCase().trim();
    const key = cleanSeat.startsWith('custom_') ? cleanSeat : ('custom_' + cleanSeat);
    const remoteObj = {
      id: key,
      name: name || `Player ${cleanSeat.replace(/\D/g, '') || ''}`,
      title: 'Remote Cribbage Persona',
      badgeColor: '#f59e0b',
      bgColor: 'linear-gradient(135deg, #1e1b4b, #312e81)',
      category: 'Remote',
      isCustom: true,
      dataUrl,
      svg: AvatarManager.generateCustomAvatarSvg(dataUrl)
    };

    AvatarManager._remoteAvatars.set(key, remoteObj);
    AvatarManager._medallionCache?.delete(`${key}_256`);
    AvatarManager._bodyWrapCache?.delete(`${key}_512x256`);

    AvatarManager.updateBoardPegPattern(cleanSeat, key);
    if (typeof window !== 'undefined' && window._currentRenderer3D?.updatePegAvatars) {
      window._currentRenderer3D.updatePegAvatars();
    }
    return remoteObj;
  }

  /**
   * Generates clean SVG markup with embedded image and circular clip path for custom photos.
   * @param {string|null} dataUrl
   * @returns {string}
   */
  static generateCustomAvatarSvg(dataUrl) {
    if (!dataUrl) {
      return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="48" fill="#1e293b" stroke="#f59e0b" stroke-width="2.5"/>
        <circle cx="50" cy="40" r="16" fill="#94a3b8"/>
        <path d="M26 80 C26 62, 74 62, 74 80 Z" fill="#94a3b8"/>
      </svg>`;
    }
    const safeHref = String(dataUrl).replace(/"/g, '&quot;');
    return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <defs>
        <clipPath id="customClip_avatar">
          <circle cx="50" cy="50" r="46"/>
        </clipPath>
      </defs>
      <circle cx="50" cy="50" r="48" fill="#0f172a" stroke="#f59e0b" stroke-width="2.5"/>
      <image href="${safeHref}" xlink:href="${safeHref}" x="4" y="4" width="92" height="92" preserveAspectRatio="xMidYMid slice" clip-path="url(#customClip_avatar)"/>
      <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(245, 158, 11, 0.45)" stroke-width="1.5"/>
    </svg>`;
  }

  /**
   * Processes, center-crops, and downscales an uploaded image file.
   * Disabled: custom avatar uploads are no longer supported.
   * @throws {Error}
   */
  static async processUploadedImage() {
    throw new Error('Custom avatar uploads are disabled');
  }

  /**
   * Retrieves the current user's selected avatar ID from storage.
   * @param {Object} [storage=null]
   * @returns {string}
   */
  static getSelectedAvatarId(storage = null) {
    const s = storage || getStorage();
    if (!s) return AvatarManager.DEFAULT_AVATAR_ID;
    try {
      const val = s.getItem(AvatarManager.STORAGE_KEY);
      if (val && typeof val === 'string') {
        const trimmed = val.trim().toLowerCase();
        if (trimmed === 'custom' || PRESET_AVATARS.some(a => a.id === trimmed)) {
          return trimmed;
        }
      }
    } catch (e) {
      console.warn('[AvatarManager] Storage read warning:', e);
    }
    return AvatarManager.DEFAULT_AVATAR_ID;
  }

  /**
   * Persists selected avatar ID into storage and dispatches UI synchronization.
   * Also broadcasts avatar updates across active WebRTC multiplayer connections.
   * @param {string} id
   * @param {Object} [storage=null]
   * @returns {string}
   */
  static setSelectedAvatarId(id, storage = null) {
    const valid = AvatarManager.getAvatarById(id).id;
    const s = storage || getStorage();
    if (s) {
      try {
        s.setItem(AvatarManager.STORAGE_KEY, valid);
      } catch (e) {
        console.warn('[AvatarManager] Storage write warning:', e);
      }
    }
    if (typeof localStorage !== 'undefined' && s !== localStorage) {
      try {
        localStorage.setItem(AvatarManager.STORAGE_KEY, valid);
      } catch (e) {}
    }

    AvatarManager.syncAvatarUI(storage);

    // Phase 2: Synchronize 2D board peg patterns in real-time
    AvatarManager.updateBoardPegPattern('p1', valid);
    AvatarManager.updateBoardPegPattern('you', valid);
    AvatarManager.updateBoardPegPattern('team1', valid);

    // Phase 3: Active 3D board peg textures
    if (typeof window !== 'undefined' && window._currentRenderer3D?.updatePegAvatars) {
      window._currentRenderer3D.updatePegAvatars();
    }

    // Phase 4: Multiplayer sync broadcast across active PeerJS connection
    if (typeof window !== 'undefined') {
      const net = window.net;
      const isOnline = (typeof window.isOnline === 'function' ? window.isOnline() : Boolean(net?.ready && net?.peer));
      if (isOnline) {
        const mySeatId = (typeof window.mySeat === 'function' ? window.mySeat() : (net?.mySeatId || 'p1'));
        const customData = (valid === 'custom') ? AvatarManager.getCustomAvatarData(storage) : null;
        const updateMsg = {
          type: 'avatar_update',
          seatId: mySeatId,
          avatarId: valid,
          customAvatarData: customData
        };
        if (typeof window.netSend === 'function') {
          window.netSend(updateMsg);
        } else if (typeof window.NetworkManager !== 'undefined' && typeof window.NetworkManager.netSend === 'function') {
          window.NetworkManager.netSend(updateMsg);
        }
      }
      window.dispatchEvent(new CustomEvent('avatar-changed', { detail: { avatarId: valid } }));
    }

    return valid;
  }

  /**
   * Updates an active SVG board's peg pattern definition in real-time.
   * @param {string} who
   * @param {string} avatarId
   */
  static updateBoardPegPattern(who, avatarId) {
    if (typeof document === 'undefined') return;
    const patId = `pegAvatarPattern_${who}`;
    const patternEl = document.getElementById(patId);
    const avatar = AvatarManager.getAvatarById(avatarId);
    const innerContent = avatar.svg.replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '');
    if (patternEl) {
      patternEl.innerHTML = innerContent;
    }
  }

  /**
   * Returns full object for selected avatar.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  static getSelectedAvatar(storage = null) {
    return AvatarManager.getAvatarById(AvatarManager.getSelectedAvatarId(storage));
  }

  /**
   * Renders raw scalable SVG markup for an avatar.
   * @param {string} id
   * @param {Object} [options={}]
   * @returns {string}
   */
  static renderAvatarSvg(id, options = {}) {
    const avatar = AvatarManager.getAvatarById(id);
    const size = options.size || 32;
    const className = options.className || 'avatar-svg';
    let raw = avatar.svg;
    if (size) {
      raw = raw.replace('<svg ', `<svg width="${size}" height="${size}" class="${className}" style="display:block;border-radius:50%;" `);
    }
    return raw;
  }

  /**
   * Renders an avatar badge component (container + SVG + glow ring).
   * @param {string} id
   * @param {Object} [options={}]
   * @returns {string}
   */
  static renderAvatarBadge(id, options = {}) {
    const avatar = AvatarManager.getAvatarById(id);
    const size = options.size || 32;
    const showRing = options.showRing !== false;
    const ringColor = options.ringColor || avatar.badgeColor || '#f0c64a';
    const borderStyle = showRing ? `border: 2px solid ${ringColor}; box-shadow: 0 0 10px ${ringColor}44;` : '';
    const className = options.className || 'cc-avatar-badge';

    return `
      <div class="${className}" data-avatar-id="${avatar.id}" title="${avatar.name} — ${avatar.title}" style="display:inline-flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;border-radius:50%;background:${avatar.bgColor};${borderStyle}overflow:hidden;flex-shrink:0;vertical-align:middle;">
        ${AvatarManager.renderAvatarSvg(avatar.id, { size: size - (showRing ? 4 : 0) })}
      </div>
    `.trim();
  }

  /**
   * Synchronizes all avatar preview elements across the DOM.
   */
  static syncAvatarUI(storage = null) {
    if (typeof document === 'undefined') return;
    const avatar = AvatarManager.getSelectedAvatar(storage);

    // 1. Lobby button preview next to #optPlayerName
    const lobbyPreviewEl = document.getElementById('playerAvatarPreview');
    if (lobbyPreviewEl) {
      lobbyPreviewEl.innerHTML = AvatarManager.renderAvatarBadge(avatar.id, { size: 38, showRing: true });
    }

    // 2. In-game score chip (#boardTopChips .score-chip.you or #scoreChipAvatarYou)
    const scoreChipAvatar = document.getElementById('scoreChipAvatarYou');
    if (scoreChipAvatar) {
      scoreChipAvatar.innerHTML = AvatarManager.renderAvatarBadge(avatar.id, { size: 22, showRing: false });
    }

    // 3. Hand zone title badge (#youZoneAvatar)
    const youZoneAvatar = document.getElementById('youZoneAvatar');
    if (youZoneAvatar) {
      youZoneAvatar.innerHTML = AvatarManager.renderAvatarBadge(avatar.id, { size: 24, showRing: true });
    }

    // 4. Online roster preview if present
    const rosterRows = document.querySelectorAll('.net-roster-row.you .net-roster-avatar');
    rosterRows.forEach(el => {
      el.innerHTML = AvatarManager.renderAvatarBadge(avatar.id, { size: 26, showRing: true });
    });

    // 5. Active board peg patterns
    AvatarManager.updateBoardPegPattern('p1', avatar.id);
    AvatarManager.updateBoardPegPattern('you', avatar.id);
    AvatarManager.updateBoardPegPattern('team1', avatar.id);

    // 6. Active 3D board peg textures
    if (typeof window !== 'undefined' && window._currentRenderer3D?.updatePegAvatars) {
      window._currentRenderer3D.updatePegAvatars();
    }
  }

  /**
   * Opens the interactive Avatar Selection Modal.
   * @param {Object} [options={}]
   */
  static openAvatarModal(options = {}) {
    if (typeof document === 'undefined') return;

    let overlay = document.getElementById('avatarModalOverlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'avatarModalOverlay';
      overlay.className = 'avatar-modal-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-labelledby', 'avatarModalTitle');
      document.body.appendChild(overlay);
    }

    // Fullscreen / 3D Room / Modal Container Reparenting & Stacking Context
    const fsElem = (typeof document !== 'undefined')
      ? (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement)
      : null;
    const b3dElem = (typeof document !== 'undefined' && typeof document.querySelector === 'function')
      ? document.querySelector('.board-3d-container.is-fullscreen, .board-3d-container:fullscreen')
      : null;
    const activeModalOverlay = document.getElementById('overlay');
    const modalParent = (activeModalOverlay && activeModalOverlay.parentElement && activeModalOverlay.parentElement !== document.body)
      ? activeModalOverlay.parentElement
      : null;
    const b3dContainer = document.getElementById('board3DContainer');
    const is3DFullscreen = Boolean(
      fsElem || b3dElem || (b3dContainer && (b3dContainer.classList?.contains?.('is-fullscreen') || (typeof document !== 'undefined' && document.body?.classList?.contains?.('is-3d-fullscreen'))))
    );

    const targetParent = fsElem || b3dElem || modalParent || (is3DFullscreen ? b3dContainer : null);
    if (targetParent && overlay.parentElement !== targetParent) {
      targetParent.appendChild(overlay);
    } else if (!targetParent && overlay.parentElement !== document.body && document.body) {
      document.body.appendChild(overlay);
    }

    overlay.style.zIndex = '20000000';

    if (!window._avatarEscapeBound && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window._avatarEscapeBound = true;
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const el = document.getElementById('avatarModalOverlay');
          if (el && el.style.display !== 'none') {
            AvatarManager.closeAvatarModal();
          }
        }
      });
    }

    const currentId = AvatarManager.getSelectedAvatarId();
    let tempSelectedId = (currentId === 'custom' || !PRESET_AVATARS.some(a => a.id === currentId))
      ? AvatarManager.DEFAULT_AVATAR_ID
      : currentId;

    const render = () => {
      const selected = AvatarManager.getAvatarById(tempSelectedId);
      const playerNameEl = document.getElementById('optPlayerName');
      const playerName = (playerNameEl && playerNameEl.value.trim()) || 'Player';

      overlay.innerHTML = `
        <div class="avatar-modal-card">
          <div class="avatar-modal-header">
            <div class="avatar-modal-title-group">
              <span class="avatar-modal-title-icon">🎭</span>
              <div>
                <h2 id="avatarModalTitle" class="avatar-modal-title">Choose Your Avatar</h2>
                <div class="avatar-modal-subtitle">Select your cribbage persona for pegs, boards, and scorecards</div>
              </div>
            </div>
            <button type="button" class="avatar-modal-close-btn" id="btnAvatarModalClose" aria-label="Close modal">✕</button>
          </div>

          <!-- Live Spotlight Preview Bar with 3D Turntable -->
          <div class="avatar-spotlight-bar" id="avatarSpotlightBar" style="background:${selected.bgColor};border:1.5px solid ${selected.badgeColor};">
            <div class="avatar-spotlight-badge" id="avatarSpotlightBadge">
              ${AvatarManager.renderAvatarBadge(selected.id, { size: 68, showRing: true, ringColor: '#facc15' })}
            </div>
            <div class="avatar-spotlight-info">
              <div class="avatar-spotlight-player">Playing as <strong>${playerName}</strong></div>
              <div class="avatar-spotlight-name" id="avatarSpotlightName" style="color:#ffffff;">${selected.name}</div>
              <div class="avatar-spotlight-tag" id="avatarSpotlightTag" style="color:#fde047;">${selected.title}</div>
            </div>
            <div class="avatar-spotlight-3d">
              <div class="turntable-header">
                <span class="turntable-icon">🎲</span>
                <span>3D Peg</span>
              </div>
              <canvas id="avatar3DTurntableCanvas" class="avatar-3d-canvas" width="100" height="100" title="Interactive 3D Peg - Drag to spin"></canvas>
              <div class="turntable-hint">Drag to rotate 360°</div>
            </div>
          </div>

          <!-- Avatar Grid -->
          <div class="avatar-modal-body">
            <div class="avatar-grid" id="avatarGrid">
              ${PRESET_AVATARS.map(a => {
                const isSelected = a.id === tempSelectedId;
                return `
                  <div class="avatar-card ${isSelected ? 'active' : ''}" data-avatar-id="${a.id}" tabindex="0" role="button" aria-pressed="${isSelected}">
                    <div class="avatar-card-badge-wrap" style="background:${a.bgColor};">
                      ${AvatarManager.renderAvatarSvg(a.id, { size: 54 })}
                      ${isSelected ? `<span class="avatar-card-check">✓</span>` : ''}
                    </div>
                    <div class="avatar-card-name">${a.name}</div>
                    <div class="avatar-card-title">${a.title}</div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="avatar-modal-footer">
            <button type="button" class="btn-ghost avatar-modal-btn" id="btnAvatarCancel">Cancel</button>
            <button type="button" class="btn-gold avatar-modal-btn" id="btnAvatarConfirm">Confirm Avatar</button>
          </div>
        </div>
      `;

      // Overlay backdrop click to close
      overlay.onclick = (e) => {
        if (e.target === overlay) AvatarManager.closeAvatarModal();
      };

      // Close button
      const closeBtn = document.getElementById('btnAvatarModalClose');
      if (closeBtn) closeBtn.onclick = () => AvatarManager.closeAvatarModal();

      const cancelBtn = document.getElementById('btnAvatarCancel');
      if (cancelBtn) cancelBtn.onclick = () => AvatarManager.closeAvatarModal();

      // Confirm button
      const confirmBtn = document.getElementById('btnAvatarConfirm');
      if (confirmBtn) {
        confirmBtn.onclick = () => {
          AvatarManager.setSelectedAvatarId(tempSelectedId);
          AvatarManager.closeAvatarModal();
          if (typeof options.onSelect === 'function') {
            options.onSelect(tempSelectedId);
          }
        };
      }

      // Card selection updates spotlight bar and turntable smoothly without recreating DOM
      const cards = overlay.querySelectorAll('.avatar-card');
      const updateSelection = (id) => {
        tempSelectedId = id;
        const item = AvatarManager.getAvatarById(id);
        const bar = document.getElementById('avatarSpotlightBar');
        if (bar) {
          bar.style.background = item.bgColor;
          bar.style.borderColor = item.badgeColor;
        }
        const badge = document.getElementById('avatarSpotlightBadge');
        if (badge) {
          badge.innerHTML = AvatarManager.renderAvatarBadge(item.id, { size: 68, showRing: true, ringColor: '#facc15' });
        }
        const nameEl = document.getElementById('avatarSpotlightName');
        if (nameEl) nameEl.textContent = item.name;
        const tagEl = document.getElementById('avatarSpotlightTag');
        if (tagEl) tagEl.textContent = item.title;

        cards.forEach(c => {
          const isAct = c.getAttribute('data-avatar-id') === id;
          c.classList.toggle('active', isAct);
          c.setAttribute('aria-pressed', isAct ? 'true' : 'false');
          let check = c.querySelector('.avatar-card-check');
          if (isAct && !check) {
            const wrap = c.querySelector('.avatar-card-badge-wrap');
            if (wrap) wrap.insertAdjacentHTML('beforeend', `<span class="avatar-card-check">✓</span>`);
          } else if (!isAct && check) {
            check.remove();
          }
        });

        if (AvatarManager._turntable?.updateAvatar) {
          AvatarManager._turntable.updateAvatar(id);
        }
      };

      cards.forEach(card => {
        const id = card.getAttribute('data-avatar-id');
        if (id) {
          card.onclick = () => updateSelection(id);
          card.onkeydown = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              updateSelection(id);
            }
          };
        }
      });

      // Initialize 3D Turntable on spotlight canvas
      const ttCanvas = document.getElementById('avatar3DTurntableCanvas');
      if (ttCanvas) {
        AvatarManager.initTurntable(ttCanvas, tempSelectedId);
      }
    };

    render();
    overlay.style.display = 'flex';
    overlay.style.zIndex = '20000000';
    AvatarManager._activeModal = overlay;
  }

  /**
   * Closes the active Avatar Selection Modal and cleans up 3D turntable.
   */
  static closeAvatarModal() {
    if (typeof document === 'undefined') return;
    AvatarManager.destroyTurntable();
    const overlay = document.getElementById('avatarModalOverlay');
    if (overlay) {
      overlay.style.display = 'none';
      const fsElem = (typeof document !== 'undefined')
        ? (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement)
        : null;
      const b3dElem = (typeof document !== 'undefined' && typeof document.querySelector === 'function')
        ? document.querySelector('.board-3d-container.is-fullscreen, .board-3d-container:fullscreen')
        : null;
      if (overlay.parentElement !== document.body && !fsElem && !b3dElem && document.body) {
        document.body.appendChild(overlay);
      }
    }
    AvatarManager._activeModal = null;
  }

  /**
   * Phase 2: Creates SVG <pattern> definition for wrapping cribbage pegs on 2D SVG boards.
   * @param {string} id
   * @param {string} patternId
   * @returns {string}
   */
  static getAvatarPatternSvg(id, patternId = 'pegAvatarPattern') {
    const avatar = AvatarManager.getAvatarById(id);
    const innerContent = avatar.svg.replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '');
    return `
      <pattern id="${patternId}" patternUnits="objectBoundingBox" width="1" height="1" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
        ${innerContent}
      </pattern>
    `.trim();
  }

  /**
   * Phase 3 & 4: Creates an HTMLCanvasElement rendering the avatar SVG or custom photo for Babylon.js DynamicTexture wrapping.
   * @param {string} id
   * @param {number} [width=128]
   * @param {number} [height=128]
   * @returns {Promise<HTMLCanvasElement|null>}
   */
  static async createAvatarCanvas(id, width = 128, height = 128) {
    if (typeof document === 'undefined') return null;
    const avatar = AvatarManager.getAvatarById(id);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    // Custom uploaded photo with direct dataUrl
    if (avatar.dataUrl) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, width, height);
          ctx.save();
          ctx.beginPath();
          ctx.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(img, 0, 0, width, height);
          ctx.restore();
          resolve(canvas);
        };
        img.onerror = () => resolve(canvas);
        img.src = avatar.dataUrl;
      });
    }

    return new Promise((resolve) => {
      const img = new Image();
      const svgBlob = new Blob([avatar.svg], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      img.onload = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        resolve(canvas);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(canvas);
      };
      img.src = url;
    });
  }

  /**
   * Phase 3: Generates a 256x256 circular coin medallion texture with milled metallic rim,
   * cameo relief portrait, and specular dome highlight for the top of 3D cribbage pegs.
   * @param {string} id - Avatar preset ID
   * @param {number} [size=256] - Medallion diameter in pixels
   * @returns {Promise<HTMLCanvasElement|null>}
   */
  static async createAvatarMedallionCanvas(id, size = 256) {
    if (typeof document === 'undefined') return null;
    AvatarManager._medallionCache = AvatarManager._medallionCache || new Map();
    const cacheKey = `${id}_${size}`;
    if (AvatarManager._medallionCache.has(cacheKey)) {
      return AvatarManager._medallionCache.get(cacheKey);
    }

    const avatar = AvatarManager.getAvatarById(id);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    const cx = size / 2;
    const cy = size / 2;
    const rOuter = size * 0.48;

    // 1. Milled metallic outer rim
    const gradRim = ctx.createLinearGradient(0, 0, size, size);
    gradRim.addColorStop(0, '#fef08a');
    gradRim.addColorStop(0.35, '#ca8a04');
    gradRim.addColorStop(0.7, '#eab308');
    gradRim.addColorStop(1, '#854d0e');

    ctx.beginPath();
    ctx.arc(cx, cy, rOuter, 0, Math.PI * 2);
    ctx.fillStyle = gradRim;
    ctx.fill();

    // Radial milled notches (coin knurling)
    const notches = 48;
    ctx.strokeStyle = 'rgba(69, 26, 3, 0.55)';
    ctx.lineWidth = 1.6;
    for (let i = 0; i < notches; i++) {
      const angle = (i * Math.PI * 2) / notches;
      const x1 = cx + Math.cos(angle) * (rOuter - 5);
      const y1 = cy + Math.sin(angle) * (rOuter - 5);
      const x2 = cx + Math.cos(angle) * rOuter;
      const y2 = cy + Math.sin(angle) * rOuter;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }

    // Inner embossed ridge
    ctx.beginPath();
    ctx.arc(cx, cy, rOuter - 6, 0, Math.PI * 2);
    ctx.strokeStyle = '#fef08a';
    ctx.lineWidth = 2.2;
    ctx.stroke();

    // 2. Cameo circle background with radial contrast
    const rInner = rOuter - 10;
    const gradBg = ctx.createRadialGradient(cx, cy * 0.85, 4, cx, cy, rInner);
    gradBg.addColorStop(0, avatar.badgeColor || '#a855f7');
    gradBg.addColorStop(0.85, '#0f172a');
    gradBg.addColorStop(1, '#020617');

    ctx.beginPath();
    ctx.arc(cx, cy, rInner, 0, Math.PI * 2);
    ctx.fillStyle = gradBg;
    ctx.fill();

    // 3. Cameo avatar portrait drawing
    const avatarCanvas = await AvatarManager.createAvatarCanvas(id, Math.round(rInner * 1.85), Math.round(rInner * 1.85));
    if (avatarCanvas) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, rInner - 1, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(avatarCanvas, cx - rInner * 0.925, cy - rInner * 0.925, rInner * 1.85, rInner * 1.85);
      ctx.restore();
    }

    // 4. Specular gloss dome / highlight
    const gradGloss = ctx.createRadialGradient(cx, cy * 0.6, 2, cx, cy * 0.6, rInner * 0.9);
    gradGloss.addColorStop(0, 'rgba(255, 255, 255, 0.45)');
    gradGloss.addColorStop(0.45, 'rgba(255, 255, 255, 0.08)');
    gradGloss.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.beginPath();
    ctx.arc(cx, cy, rInner, 0, Math.PI * 2);
    ctx.fillStyle = gradGloss;
    ctx.fill();

    // Subtle dark outer border ring
    ctx.beginPath();
    ctx.arc(cx, cy, rOuter, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.lineWidth = 1;
    ctx.stroke();

    AvatarManager._medallionCache.set(cacheKey, canvas);
    return canvas;
  }

  /**
   * Phase 3: Generates a 512x256 cylindrical wrap texture featuring dual portrait cameos (front & back),
   * gold filigree collars, and card suit trims for the body of 3D cribbage pegs.
   * @param {string} id - Avatar preset ID
   * @param {number} [width=512]
   * @param {number} [height=256]
   * @returns {Promise<HTMLCanvasElement|null>}
   */
  static async createAvatarBodyWrapCanvas(id, width = 512, height = 256) {
    if (typeof document === 'undefined') return null;
    AvatarManager._bodyWrapCache = AvatarManager._bodyWrapCache || new Map();
    const cacheKey = `${id}_${width}x${height}`;
    if (AvatarManager._bodyWrapCache.has(cacheKey)) {
      return AvatarManager._bodyWrapCache.get(cacheKey);
    }

    const avatar = AvatarManager.getAvatarById(id);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    // 1. Base vertical gradient
    const bgGrad = ctx.createLinearGradient(0, 0, 0, height);
    bgGrad.addColorStop(0, '#0f172a');
    bgGrad.addColorStop(0.2, avatar.badgeColor || '#6366f1');
    bgGrad.addColorStop(0.5, '#1e1b4b');
    bgGrad.addColorStop(0.85, '#090d16');
    bgGrad.addColorStop(1, '#020617');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, width, height);

    // 2. Top and bottom ornamental gold trim bands
    const trimH = 26;

    // Top gold band
    const topGrad = ctx.createLinearGradient(0, 0, 0, trimH);
    topGrad.addColorStop(0, '#fef08a');
    topGrad.addColorStop(0.5, '#ca8a04');
    topGrad.addColorStop(1, '#78350f');
    ctx.fillStyle = topGrad;
    ctx.fillRect(0, 0, width, trimH);
    ctx.fillStyle = '#fde047';
    ctx.fillRect(0, trimH - 2, width, 2);

    // Bottom gold band
    const botGrad = ctx.createLinearGradient(0, height - trimH - 4, 0, height);
    botGrad.addColorStop(0, '#78350f');
    botGrad.addColorStop(0.5, '#ca8a04');
    botGrad.addColorStop(1, '#fef08a');
    ctx.fillStyle = botGrad;
    ctx.fillRect(0, height - trimH - 4, width, trimH + 4);
    ctx.fillStyle = '#fde047';
    ctx.fillRect(0, height - trimH - 4, width, 2);

    // Decorative rivets along trim bands
    ctx.fillStyle = '#fef08a';
    for (let x = 8; x < width; x += 16) {
      ctx.beginPath();
      ctx.arc(x, trimH / 2, 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.beginPath();
      ctx.arc(x, height - (trimH / 2) - 2, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // 3. Render dual cameos (front face at U=0.25 and back face at U=0.75)
    const cameoR = Math.round((height - trimH * 2) * 0.35);
    const avatarCanvas = await AvatarManager.createAvatarCanvas(id, cameoR * 2, cameoR * 2);
    const centers = [width * 0.25, width * 0.75];
    const cy = height * 0.51;

    centers.forEach(cx => {
      // Outer gold filigree frame
      ctx.beginPath();
      ctx.arc(cx, cy, cameoR + 5, 0, Math.PI * 2);
      ctx.fillStyle = '#ca8a04';
      ctx.fill();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = '#fef08a';
      ctx.stroke();

      // Cameo background
      ctx.beginPath();
      ctx.arc(cx, cy, cameoR, 0, Math.PI * 2);
      ctx.fillStyle = '#0f172a';
      ctx.fill();

      if (avatarCanvas) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, cameoR, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(avatarCanvas, cx - cameoR, cy - cameoR, cameoR * 2, cameoR * 2);
        ctx.restore();
      }

      // Glass specular highlight
      const g = ctx.createLinearGradient(cx - cameoR, cy - cameoR, cx + cameoR, cy + cameoR);
      g.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
      g.addColorStop(0.4, 'rgba(255, 255, 255, 0.05)');
      g.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.beginPath();
      ctx.arc(cx, cy, cameoR, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();

      // Persona name label under cameo
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#000000';
      ctx.fillText(avatar.name, cx + 1, cy + cameoR + 15);
      ctx.fillStyle = '#fde047';
      ctx.fillText(avatar.name, cx, cy + cameoR + 14);
    });

    // 4. Cribbage card suit accents between cameos
    ctx.font = '16px serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(250, 204, 21, 0.65)';
    [width * 0.02, width * 0.5, width * 0.98].forEach(x => {
      ctx.fillText('♦', x, cy - 20);
      ctx.fillText('♠', x, cy + 20);
    });

    AvatarManager._bodyWrapCache.set(cacheKey, canvas);
    return canvas;
  }

  /**
   * Phase 3: Initializes interactive 3D Babylon.js Turntable inside the Avatar Modal spotlight bar.
   * Provides 360° auto-spin, pointer drag rotation, and live persona switching.
   * @param {HTMLCanvasElement} canvas
   * @param {string} avatarId
   * @returns {Promise<Object|null>}
   */
  static async initTurntable(canvas, avatarId) {
    if (!canvas || typeof window === 'undefined') return null;
    AvatarManager.destroyTurntable();

    let BABYLON = window.BABYLON;
    if (!BABYLON || !BABYLON.Engine) {
      if (typeof window.BoardRenderer3D !== 'undefined' && typeof window.BoardRenderer3D.prototype?.loadBabylon === 'function') {
        try {
          BABYLON = await window.BoardRenderer3D.prototype.loadBabylon.call({});
        } catch (_) {}
      }
      if (!BABYLON || !BABYLON.Engine) {
        try {
          await new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = 'dist/babylon.min.js';
            s.onload = () => res();
            s.onerror = (e) => rej(e);
            document.head.appendChild(s);
          });
          BABYLON = window.BABYLON;
        } catch (_) {}
      }
    }

    if (!BABYLON || !BABYLON.Engine) return null;

    try {
      const engine = new BABYLON.Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true, alpha: true });
      const scene = new BABYLON.Scene(engine);
      scene.clearColor = new BABYLON.Color4(0, 0, 0, 0);

      const camera = new BABYLON.ArcRotateCamera('ttCam', -Math.PI / 2, Math.PI / 2.8, 5.4, new BABYLON.Vector3(0, 0.95, 0), scene);
      camera.lowerRadiusLimit = 3.5;
      camera.upperRadiusLimit = 8.0;
      camera.wheelPrecision = 40;
      camera.attachControl(canvas, true);

      const hemi = new BABYLON.HemisphericLight('ttHemi', new BABYLON.Vector3(0, 1, 0), scene);
      hemi.intensity = 0.95;
      const dir = new BABYLON.DirectionalLight('ttDir', new BABYLON.Vector3(-1, 2, -1), scene);
      dir.intensity = 1.05;
      const point = new BABYLON.PointLight('ttPt', new BABYLON.Vector3(2, 2, 2), scene);
      point.intensity = 0.55;

      const pegRoot = new BABYLON.TransformNode('ttPegRoot', scene);

      // Shank (tapered lower peg tip)
      const shank = BABYLON.MeshBuilder.CreateCylinder('ttShank', {
        height: 0.5,
        diameterTop: 0.52,
        diameterBottom: 0.44,
        tessellation: 24
      }, scene);
      shank.position.y = -0.25;
      shank.parent = pegRoot;

      // Cylinder body (textured with avatar wrap)
      const body = BABYLON.MeshBuilder.CreateCylinder('ttBody', {
        height: 1.8,
        diameterTop: 0.68,
        diameterBottom: 0.82,
        tessellation: 32
      }, scene);
      body.position.y = 0.9;
      body.parent = pegRoot;

      // Spherical collar
      const cap = BABYLON.MeshBuilder.CreateSphere('ttCap', {
        diameter: 0.92,
        segments: 20
      }, scene);
      cap.position.y = 1.8;
      cap.parent = pegRoot;

      // Top coin medallion disc
      const medallion = BABYLON.MeshBuilder.CreateCylinder('ttMedallion', {
        height: 0.12,
        diameter: 0.94,
        tessellation: 32
      }, scene);
      medallion.position.y = 2.22;
      medallion.parent = pegRoot;

      // PBR Metallic Gold material for shank and cap
      const matGold = new BABYLON.PBRMaterial('ttMatGold', scene);
      matGold.albedoColor = new BABYLON.Color3(0.92, 0.74, 0.2);
      matGold.metallic = 0.92;
      matGold.roughness = 0.18;
      if (matGold.clearCoat) {
        matGold.clearCoat.isEnabled = true;
        matGold.clearCoat.intensity = 0.6;
      }
      shank.material = matGold;
      cap.material = matGold;

      // PBR Material for body wrap
      const matBody = new BABYLON.PBRMaterial('ttMatBody', scene);
      matBody.metallic = 0.25;
      matBody.roughness = 0.35;
      body.material = matBody;

      // PBR Material for top coin medallion
      const matMedallion = new BABYLON.PBRMaterial('ttMatMedallion', scene);
      matMedallion.metallic = 0.45;
      matMedallion.roughness = 0.22;
      medallion.material = matMedallion;

      let currentDynBody = null;
      let currentDynMed = null;

      const updateTextures = async (currId) => {
        try {
          const [bodyCanvas, medCanvas] = await Promise.all([
            AvatarManager.createAvatarBodyWrapCanvas(currId),
            AvatarManager.createAvatarMedallionCanvas(currId)
          ]);
          if (scene.isDisposed) return;

          if (bodyCanvas) {
            if (currentDynBody) currentDynBody.dispose();
            currentDynBody = new BABYLON.DynamicTexture(`ttDynBody_${currId}`, { width: bodyCanvas.width, height: bodyCanvas.height }, scene, true);
            const bctx = currentDynBody.getContext();
            bctx.drawImage(bodyCanvas, 0, 0);
            currentDynBody.update();
            matBody.albedoTexture = currentDynBody;
          }

          if (medCanvas) {
            if (currentDynMed) currentDynMed.dispose();
            currentDynMed = new BABYLON.DynamicTexture(`ttDynMed_${currId}`, { width: medCanvas.width, height: medCanvas.height }, scene, true);
            const mctx = currentDynMed.getContext();
            mctx.drawImage(medCanvas, 0, 0);
            currentDynMed.update();
            matMedallion.albedoTexture = currentDynMed;
          }
        } catch (e) {
          console.warn('[AvatarManager] Turntable texture update error:', e);
        }
      };

      await updateTextures(avatarId);

      let isDragging = false;
      const onDown = () => { isDragging = true; };
      const onUp = () => { isDragging = false; };
      canvas.addEventListener('pointerdown', onDown);
      window.addEventListener('pointerup', onUp);

      const renderCallback = () => {
        if (!scene.isDisposed) {
          if (!isDragging) {
            pegRoot.rotation.y += 0.016;
          }
          scene.render();
        }
      };
      engine.runRenderLoop(renderCallback);

      const onResize = () => engine.resize();
      window.addEventListener('resize', onResize);

      const turntableObj = {
        engine,
        scene,
        pegRoot,
        updateAvatar: (newId) => updateTextures(newId),
        destroy: () => {
          canvas.removeEventListener('pointerdown', onDown);
          window.removeEventListener('pointerup', onUp);
          window.removeEventListener('resize', onResize);
          if (currentDynBody) currentDynBody.dispose();
          if (currentDynMed) currentDynMed.dispose();
          scene.dispose();
          engine.dispose();
        }
      };

      AvatarManager._turntable = turntableObj;
      return turntableObj;
    } catch (err) {
      console.warn('[AvatarManager] Could not initialize 3D turntable:', err);
      return null;
    }
  }

  /**
   * Destroys active 3D turntable instance and frees WebGL resources.
   */
  static destroyTurntable() {
    if (AvatarManager._turntable) {
      try {
        AvatarManager._turntable.destroy();
      } catch (_) {}
      AvatarManager._turntable = null;
    }
  }
}

if (typeof window !== 'undefined') {
  window.AvatarManager = AvatarManager;
  window.openAvatarModal = (options) => AvatarManager.openAvatarModal(options);
  window.closeAvatarModal = () => AvatarManager.closeAvatarModal();
}

